import React, { useEffect, useMemo } from "react";
import { useTopLevelSpaces } from "./useEntities/useTopLevelSpaces";
import {
  EntityLibrary,
  EntityLibraryProps,
} from "./EntityLibrary/EntityLibrary";
import { Space } from "@highnote/server/src/core/entities";
import { getSpaceTrackRowId } from "@highnote/server/src/core/shared-util";
import { useChildEntityWatchers } from "App/store/helpers/useChildEntityWatchers";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { useGlobalSpaceTracks } from "App/store/spaceTracks/useGlobalSpaceTracks";
import { useGlobalTracks } from "App/store/tracks/useGlobalTracks";
import {
  SpaceTrackTile,
  buildSpaceTrackTile,
} from "App/store/tracks/buildSpaceTrackTile";
import {
  useFormatSpaceToBaseEntityRow,
  useFormatTrackToBaseEntityRow,
} from "./EntityTable/ExpandableSpace/ExpandableSpaceRow";
import { asViewingSpaceId } from "App/modules/viewingSpace";

const CLICK_THRU_TABLE_ROW_VARIANT = "nested-click-thru";

export const ClickThruSpaceRowComponent = ({
  parentSpace,
  tableBaseProps,
}: {
  parentSpace?: Space;
  tableBaseProps: Omit<Omit<EntityLibraryProps, "spaceRows">, "trackRows">;
}) => {
  const { topLevelSpaces } = useTopLevelSpaces();

  const { manageGlobalChildEntityWatchers } = useChildEntityWatchers();
  const { globalSpaces } = useGlobalSpaces();
  const { globalTracks } = useGlobalTracks();
  const { globalSpaceTracks } = useGlobalSpaceTracks();
  const { formatSpaceToBaseEntityRow } = useFormatSpaceToBaseEntityRow();
  const { formatTrackToBaseEntityRow } = useFormatTrackToBaseEntityRow();

  const spaceRows = useMemo(() => {
    if (parentSpace) {
      return (globalSpaces.childSpaces.get(parentSpace.id) || []).map(
        (spaceId) => {
          return formatSpaceToBaseEntityRow({
            space: globalSpaces.spaces.get(spaceId),
            options: {
              tableRowVariant: CLICK_THRU_TABLE_ROW_VARIANT,
            },
          });
        },
      );
    }

    return topLevelSpaces.map((topLevelSpace) => {
      return formatSpaceToBaseEntityRow({
        space: topLevelSpace,
        options: {
          tableRowVariant: CLICK_THRU_TABLE_ROW_VARIANT,
        },
      });
    });
  }, [
    parentSpace,
    topLevelSpaces,
    globalSpaces.childSpaces.get(parentSpace?.id),
  ]);

  const trackRows = (globalTracks.childTracks.get(parentSpace?.id) || [])
    // `childTracks` can hold ids for a borrowed track whose canonical Track
    // doc was never fetched by the legacy per-space query.
    // `buildSpaceTrackTile` resolves those from their space-tracks-v1 row
    // instead — a row-backed tile needs no canonical Track doc at all.
    // Only an id backed by NEITHER a row NOR a resolved Track (unreachable
    // today given how childTracks membership is populated, but defended
    // anyway) still returns `undefined` and is filtered here, rather than
    // let it reach `formatTrackToBaseEntityRow`, which dereferences fields
    // on the track unconditionally.
    .map((globalTrackId) =>
      buildSpaceTrackTile({
        row: globalSpaceTracks.spaceTrackRows.get(
          getSpaceTrackRowId(parentSpace?.id, globalTrackId),
        ),
        track: globalTracks.tracks.get(globalTrackId),
        // Minted, not route-derived: this is the space whose
        // `space-tracks-v1` rows produced this tile (same id as the
        // row lookup above), so by construction it IS the space the
        // tile is displayed under. For a nested space that is more
        // precise than `useUrlContext`, which resolves only the
        // route's top-level space.
        viewingSpaceId: asViewingSpaceId(parentSpace?.id),
      }),
    )
    .filter((tile): tile is SpaceTrackTile => Boolean(tile))
    .map((track) => {
      return formatTrackToBaseEntityRow({
        track,
        options: {
          hasPlayableTracks: false,
          tableRowVariant: CLICK_THRU_TABLE_ROW_VARIANT,
        },
      });
    });

  useEffect(() => {
    if (parentSpace) {
      // Used to fetch child entities and set realtime watchers/listeners
      // for the current parent spaceId.
      manageGlobalChildEntityWatchers({
        spaceId: parentSpace.id,
        componentId: "click-thru-space-row",
      }).attach();
    }

    return () => {
      if (parentSpace) {
        // Used to unsubscribe from realtime watchers/listeners
        // for the current parent spaceId.
        manageGlobalChildEntityWatchers({
          spaceId: parentSpace.id,
          componentId: "click-thru-space-row",
        }).detach();
      }
    };
  }, [parentSpace?.id]);

  return (
    <EntityLibrary
      {...tableBaseProps}
      spaceRows={spaceRows}
      trackRows={trackRows}
    />
  );
};
